import React, { useEffect, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  ExternalLink,
  Linkedin,
  Loader2,
  MessageCircle,
  Newspaper,
  Send,
  Share2,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { blogAPI } from '../services/api';

const CommentsSection = ({ postId, language }) => {
  const nl = language === 'nl';
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    blogAPI.getComments(postId)
      .then(setComments)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [postId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.comment.trim()) {
      setError(nl ? 'Vul je naam en reactie in.' : 'Please fill in your name and comment.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await blogAPI.addComment(postId, form);
      setSubmitted(true);
      setForm({ name: '', comment: '' });
    } catch (err) {
      setError(nl ? 'Versturen mislukt. Probeer opnieuw.' : 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-gray-100" data-testid="blog-comments-section">
      <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-blue-500" />
        {nl ? 'Reacties' : 'Comments'} ({comments.length})
      </h4>
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      ) : (
        <>
          {comments.length > 0 && (
            <div className="space-y-3 mb-5">
              {comments.map((c) => (
                <div key={c.id} className="bg-gray-50 rounded-lg p-3" data-testid="blog-comment">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900">{c.name}</span>
                    <time className="text-xs text-gray-400">
                      {new Date(c.created_at).toLocaleDateString(nl ? 'nl-BE' : 'en-GB')}
                    </time>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.comment}</p>
                </div>
              ))}
            </div>
          )}
          {submitted ? (
            <p className="text-sm text-blue-600 bg-blue-50 rounded-lg p-3" data-testid="blog-comment-success">
              {nl
                ? 'Bedankt! Je reactie wordt getoond zodra deze is goedgekeurd.'
                : 'Thanks! Your comment will appear once approved.'}
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-2" data-testid="blog-comment-form">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={nl ? 'Je naam' : 'Your name'}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                data-testid="blog-comment-name-input"
              />
              <textarea
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                placeholder={nl ? 'Schrijf een reactie...' : 'Write a comment...'}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-y"
                data-testid="blog-comment-text-input"
              />
              {error && <p className="text-xs text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
                data-testid="blog-comment-submit-btn"
              >
                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {nl ? 'Reageer' : 'Comment'}
              </button>
              <p className="text-xs text-gray-400">
                {nl ? 'Reacties worden gemodereerd voor publicatie.' : 'Comments are moderated before publication.'}
              </p>
            </form>
          )}
        </>
      )}
    </div>
  );
};

const BlogCard = ({ post, language, highlighted = false }) => {
  const nl = language === 'nl';
  const [expanded, setExpanded] = useState(highlighted);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (highlighted && cardRef.current) {
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [highlighted]);

  const handleShare = async () => {
    const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/blog/share/${post.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (_e) {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <article
      ref={cardRef}
      className={`bg-white rounded-2xl border shadow-sm p-6 mb-8 break-inside-avoid ${highlighted ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-100'}`}
      data-testid="blog-post-card"
    >
      {post.thumbnail && (
        <img
          src={post.thumbnail}
          alt={post.title}
          className="w-full h-48 object-cover rounded-xl mb-4"
          data-testid="blog-post-thumbnail"
        />
      )}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {post.source === 'linkedin' ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
            <Linkedin className="h-3.5 w-3.5" />
            {post.post_type === 'carousel' ? (nl ? 'LinkedIn carrousel' : 'LinkedIn carousel') : 'LinkedIn'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
            <Newspaper className="h-3.5 w-3.5" />
            {nl ? 'Artikel' : 'Article'}
          </span>
        )}
        <time className="text-xs text-gray-400">
          {new Date(post.created_at).toLocaleDateString(nl ? 'nl-BE' : 'en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </time>
        <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-testid="blog-post-views">
          <Eye className="h-3.5 w-3.5" />
          {post.views ?? 0}x {nl ? 'bekeken' : 'viewed'}
        </span>
        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
          <MessageCircle className="h-3.5 w-3.5" />
          {post.comment_count ?? 0}
        </span>
      </div>

      <h2 className="text-xl font-bold text-gray-900 mb-2">{post.title}</h2>
      {post.summary && <p className="text-gray-600 mb-4">{post.summary}</p>}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          data-testid="blog-expand-btn"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {expanded ? (nl ? 'Verberg bericht' : 'Hide post') : (nl ? 'Toon bericht' : 'Show post')}
        </button>
        {post.source === 'linkedin' && (post.embed_urn || post.linkedin_url) && (
          <a
            href={post.embed_urn ? `https://www.linkedin.com/feed/update/${post.embed_urn}/` : post.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 border border-blue-500 text-blue-600 hover:bg-blue-50 text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            data-testid="blog-linkedin-btn"
          >
            <Linkedin className="h-4 w-4" />
            {nl ? 'Bekijk op LinkedIn' : 'View on LinkedIn'}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        <button
          onClick={handleShare}
          className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          data-testid="blog-share-btn"
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Share2 className="h-4 w-4" />}
          {copied ? (nl ? 'Link gekopieerd!' : 'Link copied!') : (nl ? 'Deel' : 'Share')}
        </button>
      </div>

      {expanded && (
        <div className="mt-5" data-testid="blog-expanded-content">
          {post.source === 'linkedin' ? (
            <>
              {post.content && (
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap mb-5 bg-gray-50 rounded-xl p-4 border-l-4 border-blue-500" data-testid="blog-linkedin-text">
                  {post.content}
                </div>
              )}
              <div className="flex justify-center">
                <iframe
                  src={`https://www.linkedin.com/embed/feed/update/${post.embed_urn}`}
                  height={post.post_type === 'carousel' ? 800 : 570}
                  width="100%"
                  frameBorder="0"
                  allowFullScreen
                  title={post.title}
                  className="rounded-lg max-w-[560px]"
                  data-testid="blog-linkedin-embed"
                />
              </div>
            </>
          ) : (
            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">{post.content}</div>
          )}
          <CommentsSection postId={post.id} language={language} />
        </div>
      )}
    </article>
  );
};

export default function BlogPage() {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const highlightId = new URLSearchParams(window.location.search).get('post');

  useEffect(() => {
    blogAPI.getPosts()
      .then((data) => {
        setPosts(data);
        const viewed = JSON.parse(sessionStorage.getItem('blog_viewed_ids') || '[]');
        const newIds = data.map((p) => p.id).filter((id) => !viewed.includes(id));
        if (newIds.length > 0) {
          blogAPI.trackViews(newIds).catch(() => {});
          sessionStorage.setItem('blog_viewed_ids', JSON.stringify([...viewed, ...newIds]));
        }
      })
      .catch((err) => console.error('Failed to load blog posts:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50" data-testid="blog-page">
      <Header />
      <main className="flex-1">
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
          <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold mb-5 bg-blue-100 text-blue-600">
            Blog
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {language === 'nl' ? 'AI for HR inzichten' : 'AI for HR insights'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            {language === 'nl'
              ? 'Praktische inzichten, ervaringen en updates over AI in HR — rechtstreeks vanuit onze praktijk en LinkedIn.'
              : 'Practical insights, experiences and updates on AI in HR — straight from our practice and LinkedIn.'}
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100" data-testid="blog-empty-state">
              <Newspaper className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {language === 'nl' ? 'Nog geen blogposts. Kom binnenkort terug!' : 'No blog posts yet. Check back soon!'}
              </p>
            </div>
          ) : (
            <div className="columns-1 md:columns-2 gap-8">
              {posts.map((post) => (
                <BlogCard key={post.id} post={post} language={language} highlighted={post.id === highlightId} />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
